import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Loader2, FileText, Check, Calendar, DollarSign, MapPin, Music, ArrowRight } from "lucide-react";
import { format } from "date-fns";

interface ContractData {
  id: string;
  contract_number: string;
  client_name: string;
  client_email: string;
  event_details: any;
  packages: any;
  total_amount: number;
  deposit_amount: number | null;
  deposit_due_date: string | null;
  final_payment_due_date: string | null;
  includes_airfare: boolean;
  includes_accommodation: boolean;
  includes_meals: boolean;
  includes_ground_transport: boolean;
  custom_inclusions: string[] | null;
  custom_exclusions: string[] | null;
  payment_terms: string | null;
  cancellation_policy: string | null;
  force_majeure_clause: string | null;
  liability_clause: string | null;
  special_requirements: string | null;
  additional_notes: string | null;
  signed_at: string | null;
  signature_name: string | null;
  payment_status: string | null;
  payment_token: string | null;
  paid_at: string | null;
  payment_mode: string;
  balance_amount: number | null;
  balance_due_date: string | null;
  daily_late_fee: number | null;
  deposit_paid_at: string | null;
}

const SIGNATURE_FONTS = [
  { name: "Brush Script", style: "font-family: 'Brush Script MT', cursive; font-size: 32px;" },
  { name: "Lucida Handwriting", style: "font-family: 'Lucida Handwriting', cursive; font-size: 28px;" },
  { name: "Segoe Script", style: "font-family: 'Segoe Script', cursive; font-size: 28px;" },
];

export default function SignContract() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(true);
  const [isSigning, setIsSigning] = useState(false);
  const [contract, setContract] = useState<ContractData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signatureName, setSignatureName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [selectedFont, setSelectedFont] = useState(0);
  const [isRedirectingToPayment, setIsRedirectingToPayment] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid contract link. Please check your email for the correct link.");
      setIsLoading(false);
      return;
    }
    fetchContract();
  }, [token]);

  const fetchContract = async () => {
    try {
      const { data, error } = await supabase
        .from("inquiry_contracts")
        .select("*, inquiry_id")
        .eq("signing_token", token)
        .gt("signing_token_expires_at", new Date().toISOString())
        .single();

      if (error || !data) {
        setError("This contract link has expired or is invalid. Please contact us for a new link.");
        return;
      }

      setContract(data as ContractData);

      if (!data.signed_at && !data.viewed_at) {
        await supabase
          .from("inquiry_contracts")
          .update({ viewed_at: new Date().toISOString() })
          .eq("id", data.id);

        await supabase.functions.invoke("update-inquiry-status", {
          body: {
            inquiryId: data.inquiry_id,
            logActivity: {
              action: "contract_viewed",
              description: `Contract #${data.contract_number} was opened by client`,
            },
          },
        });
      }
    } catch {
      setError("Failed to load contract. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSign = async () => {
    if (!contract || !signatureName.trim() || !agreedToTerms) return;

    setIsSigning(true);
    try {
      const { data, error } = await supabase.functions.invoke("sign-contract", {
        body: {
          signingToken: token,
          signatureName: signatureName.trim(),
          signatureFont: SIGNATURE_FONTS[selectedFont].name,
          userAgent: navigator.userAgent,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to sign contract");
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to sign contract");
      }

      const paymentToken = data.paymentToken;
      if (!paymentToken) {
        throw new Error("No payment token returned from server");
      }

      toast({
        title: "Contract Signed! ✓",
        description: "Redirecting to your payment portal...",
      });

      window.location.assign(`${window.location.origin}/pay-contract?token=${paymentToken}`);
    } catch (err: any) {
      toast({
        title: "Failed to sign",
        description: err.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsSigning(false);
    }
  };

  const handleContinueToPayment = () => {
    if (!contract?.payment_token) {
      toast({
        title: "Payment link unavailable",
        description: "Please contact us for a fresh payment link.",
        variant: "destructive",
      });
      return;
    }

    setIsRedirectingToPayment(true);
    window.location.assign(`${window.location.origin}/pay-contract?token=${contract.payment_token}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <CardTitle>Contract Not Available</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} className="w-full">
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!contract) return null;

  const effectiveLateFee = contract.payment_mode === "deposit" ? Math.max(contract.daily_late_fee || 0, 20) : 0;
  const depositDueDateLabel = contract.deposit_due_date
    ? format(new Date(`${contract.deposit_due_date}T00:00:00`), "MMM d, yyyy")
    : "Upon signing";
  const voidDate = contract.deposit_due_date ? new Date(`${contract.deposit_due_date}T00:00:00`) : null;
  if (voidDate) voidDate.setDate(voidDate.getDate() + 1);
  const voidDateLabel = voidDate ? format(voidDate, "MMM d, yyyy") : depositDueDateLabel;
  const balanceDueDateLabel = contract.balance_due_date
    ? format(new Date(`${contract.balance_due_date}T00:00:00`), "MMMM d, yyyy")
    : "TBD";

  if (contract.signed_at) {
    if (contract.payment_status === "paid") {
      return (
        <div className="min-h-screen bg-background p-4 md:p-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border border-primary/30 bg-primary/5">
              <CardHeader className="text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mb-4">
                  <Check className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-primary">Payment Complete</CardTitle>
                <CardDescription>
                  Thank you! Your booking is fully confirmed.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="bg-muted/50 p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">Contract #{contract.contract_number}</p>
                  <p className="text-2xl font-bold text-primary">
                    ${contract.total_amount?.toLocaleString()} Paid
                  </p>
                  {contract.paid_at && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Paid on {format(new Date(contract.paid_at), "MMMM d, yyyy")}
                    </p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  A confirmation email has been sent to {contract.client_email}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-3xl mx-auto">
          <Card className="border-primary ring-2 ring-primary/20">
            <CardHeader className="pb-4 border-b bg-primary/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <Check className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-primary text-lg">Contract Signed</CardTitle>
                    <Badge variant="secondary" className="bg-primary/10 text-primary text-xs">
                      Step 1 Complete
                    </Badge>
                  </div>
                  <CardDescription className="text-sm">
                    Signed on {format(new Date(contract.signed_at), "MMMM d, yyyy 'at' h:mm a")} by{" "}
                    <span style={{ fontFamily: "'Brush Script MT', cursive" }} className="text-foreground">
                      {contract.signature_name}
                    </span>
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-primary text-primary-foreground">Step 2 of 2</Badge>
                <span className="font-semibold text-lg">Complete Your Payment</span>
              </div>

              <div className="text-center py-6 bg-primary/10 rounded-lg border-2 border-primary/20">
                {contract.payment_mode === "deposit" && !contract.deposit_paid_at ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-1">Deposit Due Now</p>
                    <p className="text-5xl font-bold text-primary">
                      ${contract.deposit_amount?.toLocaleString()}
                    </p>
                    <div className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <p>Total Contract Value: ${contract.total_amount?.toLocaleString()}</p>
                      <p>Remaining Balance: ${contract.balance_amount?.toLocaleString()} — due by {balanceDueDateLabel}</p>
                      <p className="text-xs text-muted-foreground">Initial deposit due by {depositDueDateLabel}</p>
                      <p className="text-xs text-destructive">Late fee of ${effectiveLateFee}/day applies after the balance due date</p>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mb-1">Total Amount Due</p>
                    <p className="text-5xl font-bold text-primary">
                      ${contract.total_amount?.toLocaleString()}
                    </p>
                  </>
                )}
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleContinueToPayment}
                  disabled={isRedirectingToPayment}
                  className="w-full h-auto py-5 justify-between"
                  size="lg"
                >
                  <span>Continue to secure payment portal</span>
                  <ArrowRight className="h-5 w-5" />
                </Button>
                <p className="text-sm text-muted-foreground text-center">
                  Choose card or ACH securely in the payment portal.
                </p>
              </div>

              {isRedirectingToPayment && (
                <div className="flex items-center justify-center gap-2 py-4 bg-muted/50 rounded-lg">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Redirecting to your payment portal...
                  </p>
                </div>
              )}

              <div className="text-center pt-2">
                <p className="text-xs text-muted-foreground">
                  🔒 Payments are securely processed by Stripe
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const eventDetails = Array.isArray(contract.event_details) ? contract.event_details : [contract.event_details];
  const packages = Array.isArray(contract.packages) ? contract.packages : [];

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">The Lost Strings Company</h1>
          <p className="text-muted-foreground">Performance Contract</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>Contract #{contract.contract_number}</CardTitle>
                <CardDescription>Prepared for {contract.client_name}</CardDescription>
              </div>
              <Badge variant="outline" className="text-primary border-primary">
                Awaiting Signature
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4" /> Event Details
              </h3>
              <div className="space-y-3">
                {eventDetails.map((evt: any, idx: number) => (
                  <div key={idx} className="bg-muted/50 p-4 rounded-lg">
                    <p className="font-medium">{evt.occasion || `Event ${idx + 1}`}</p>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                      {evt.date && <p><span className="text-muted-foreground">Date:</span> {evt.date}</p>}
                      {evt.startTime && <p><span className="text-muted-foreground">Time:</span> {evt.startTime} - {evt.endTime}</p>}
                      {evt.venueAddress && (
                        <p className="col-span-2 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {evt.venueAddress}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {packages.length > 0 && (
              <div>
                <h3 className="font-semibold flex items-center gap-2 mb-3">
                  <Music className="h-4 w-4" /> Services
                </h3>
                <div className="space-y-2">
                  {packages.map((pkg: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center bg-muted/50 p-3 rounded-lg">
                      <div>
                        <p className="font-medium">{pkg.name}</p>
                        {pkg.musicians && (
                          <p className="text-sm text-muted-foreground">
                            Musicians: {Array.isArray(pkg.musicians) ? pkg.musicians.join(", ") : pkg.musicians}
                          </p>
                        )}
                      </div>
                      <p className="font-semibold text-primary">${pkg.price?.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            <div>
              <h3 className="font-semibold flex items-center gap-2 mb-3">
                <DollarSign className="h-4 w-4" /> Payment Summary
              </h3>
              <div className="bg-primary/5 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span>Total Contract Value</span>
                  <span className="font-bold text-lg">${contract.total_amount?.toLocaleString()}</span>
                </div>
                {contract.deposit_amount && contract.deposit_amount < contract.total_amount && (
                  <>
                    <Separator />
                    <div className="flex justify-between text-sm">
                      <span>Deposit Due by {depositDueDateLabel}</span>
                      <span>${contract.deposit_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Final Payment by {contract.final_payment_due_date ? format(new Date(`${contract.final_payment_due_date}T00:00:00`), "MMM d, yyyy") : "TBD"}</span>
                      <span>${(contract.total_amount - contract.deposit_amount)?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Agreement Void Date</span>
                      <span>{voidDateLabel}</span>
                    </div>
                    <div className="flex justify-between text-sm text-destructive">
                      <span>Daily Late Fee</span>
                      <span>${effectiveLateFee.toLocaleString()} / day</span>
                    </div>
                  </>
                )}
                {(!contract.deposit_amount || contract.deposit_amount >= contract.total_amount) && contract.deposit_due_date && (
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Payment Due</span>
                    <span>{depositDueDateLabel}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2 text-primary">Included</h4>
                <ul className="text-sm space-y-1">
                  {contract.includes_airfare && <li>• Airfare for musicians/team</li>}
                  {contract.includes_accommodation && <li>• Accommodation</li>}
                  {contract.includes_meals && <li>• Meals and refreshments</li>}
                  {contract.includes_ground_transport && <li>• Ground transportation</li>}
                  {contract.custom_inclusions?.map((inc, i) => <li key={i}>• {inc}</li>)}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2 text-muted-foreground">Not Included</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  {!contract.includes_airfare && <li>• Airfare</li>}
                  {!contract.includes_accommodation && <li>• Accommodation</li>}
                  {!contract.includes_meals && <li>• Meals</li>}
                  {!contract.includes_ground_transport && <li>• Ground transportation</li>}
                  {contract.custom_exclusions?.map((exc, i) => <li key={i}>• {exc}</li>)}
                </ul>
              </div>
            </div>

            {contract.payment_terms && (
              <div>
                <h4 className="font-medium mb-2">Payment Terms</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{contract.payment_terms}</p>
              </div>
            )}

            {contract.cancellation_policy && (
              <div>
                <h4 className="font-medium mb-2">Cancellation Policy</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{contract.cancellation_policy}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle>Sign This Contract</CardTitle>
            <CardDescription>
              Type your full legal name below to electronically sign this contract
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="signature">Your Full Name</Label>
              <Input
                id="signature"
                placeholder="Type your full legal name"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                className="text-lg"
              />
            </div>

            {signatureName && (
              <div className="space-y-2">
                <Label>Signature Preview</Label>
                <div className="border rounded-lg p-6 bg-card text-center">
                  <p
                    className="text-3xl text-foreground"
                    style={{ fontFamily: "'Brush Script MT', cursive" }}
                  >
                    {signatureName}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-3">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
              />
              <label htmlFor="terms" className="text-sm leading-relaxed cursor-pointer">
                I, <strong>{signatureName || "[Your Name]"}</strong>, have read and agree to the terms and conditions
                outlined in this contract. I understand this constitutes a legally binding agreement between myself
                and The Lost Strings Company.
              </label>
            </div>

            <Button
              onClick={handleSign}
              disabled={!signatureName.trim() || !agreedToTerms || isSigning}
              className="w-full"
              size="lg"
            >
              {isSigning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Sign Contract
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              By clicking "Sign Contract", you agree to use electronic signatures and acknowledge
              that your electronic signature has the same legal effect as a handwritten signature.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
